import UnitPageShell from '../components/shared/UnitPageShell';
import React from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
  IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LaunchIcon from '@mui/icons-material/Launch';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { getCardSx } from '../theme/tokens';

type VideoEntry = {
  id: string;
  title: string;
  url: string; // embed url
  sourceUrl: string; // original url for open
  note?: string;
};

const STORAGE_KEY = 'videos_custom';

const defaultVideos: VideoEntry[] = [
  {
    id: 'yt-1',
    title: 'SE Urbach – Imagefilm',
    url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    sourceUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    note: 'Beispiel – ersetze mit echten Schulungsvideos.'
  }
];

const extractYouTubeId = (input: string): string | null => {
  if (!input) return null;
  const trimmed = input.trim();
  // if iframe code passed
  const iframeMatch = trimmed.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{6,})/i);
  if (iframeMatch) return iframeMatch[1];
  // regular watch url
  const urlMatch = trimmed.match(/[?&]v=([a-zA-Z0-9_-]{6,})/i);
  if (urlMatch) return urlMatch[1];
  // short youtu.be
  const shortMatch = trimmed.match(/youtu\.be\/([a-zA-Z0-9_-]{6,})/i);
  if (shortMatch) return shortMatch[1];
  return null;
};

const toEmbedUrl = (id: string) => `https://www.youtube.com/embed/${id}`;

const Videos: React.FC = () => {
  const { darkMode } = useTheme();
  const { canUploadVideos } = useAuth();

  const [videos, setVideos] = React.useState<VideoEntry[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) {
      // ignore parse errors
    }
    return defaultVideos;
  });

  const [title, setTitle] = React.useState('');
  const [link, setLink] = React.useState('');
  const [note, setNote] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const persist = (list: VideoEntry[]) => {
    setVideos(list);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch {}
  };

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null); setSuccess(null);
    const id = extractYouTubeId(link);
    if (!title.trim()) {
      setError('Titel fehlt.');
      return;
    }
    if (!id) {
      setError('Kein gültiger YouTube-Link oder Embed-Code erkannt.');
      return;
    }
    const embedUrl = toEmbedUrl(id);
    const entry: VideoEntry = {
      id: `${id}-${Date.now()}`,
      title: title.trim(),
      url: embedUrl,
      sourceUrl: link.trim(),
      note: note.trim() || undefined
    };
    const next = [entry, ...videos];
    persist(next);
    setTitle(''); setLink(''); setNote('');
    setSuccess('Video hinzugefügt (lokal gespeichert).');
  };

  const handleDelete = (id: string) => {
    const next = videos.filter((v) => v.id !== id);
    persist(next);
  };

  return (
    <UnitPageShell title="Videos & Tutorials">
      <Box sx={{ py: { xs: 3, md: 4 } }}>
        <Container maxWidth={false} disableGutters sx={{ px: { xs: 2, md: 4 } }}>
          <Stack spacing={3}>
            {canUploadVideos && (
            <Paper sx={getCardSx(darkMode)}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                Video hinzufügen
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Füge YouTube-Links oder Embed-Codes hinzu. Die Liste wird lokal im Browser gespeichert und ist nur für dich sichtbar.
              </Typography>
              <Box component="form" onSubmit={handleAdd} sx={{ display: 'grid', gap: 2 }}>
                {error && <Alert severity="error">{error}</Alert>}
                {success && <Alert severity="success">{success}</Alert>}
                <TextField label="Titel" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth required />
                <TextField label="YouTube-Link oder Embed-Code" value={link} onChange={(e) => setLink(e.target.value)} fullWidth required />
                <TextField label="Notiz (optional)" value={note} onChange={(e) => setNote(e.target.value)} fullWidth multiline minRows={2} />
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <Button type="submit" variant="contained" color="primary">Speichern</Button>
                  <Button type="button" variant="outlined" onClick={() => { setTitle(''); setLink(''); setNote(''); setError(null); setSuccess(null); }}>Zurücksetzen</Button>
                </Box>
              </Box>
            </Paper>
            )}

            <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
              Meine Videos
            </Typography>

            {videos.length === 0 && (
              <Alert severity="info">Noch keine Videos hinterlegt.</Alert>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
              {videos.map((v) => (
                  <Paper sx={{ ...getCardSx(darkMode), p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden' }}>
                      <iframe
                        title={v.title}
                        src={v.url}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                      />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{v.title}</Typography>
                    {v.note && <Typography variant="body2" color="text.secondary">{v.note}</Typography>}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <IconButton size="small" onClick={() => window.open(v.sourceUrl, '_blank', 'noopener,noreferrer')} title="In YouTube öffnen">
                        <LaunchIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(v.id)} title="Entfernen">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
              ))}
            </Box>
          </Stack>
        </Container>
      </Box>
    </UnitPageShell>
  );
};

export default Videos;
